export type ServiceAreaFaq = {
  question: string;
  answer: string;
};

export type ServiceArea = {
  slug: string;
  name: string;
  region: "Metro Vancouver" | "Lower Mainland";
  confirmed: boolean;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  localContext: string;
  assessmentFocus: readonly string[];
  propertyContexts: readonly string[];
  relatedServicePaths: readonly string[];
  faqs: readonly ServiceAreaFaq[];
};

/**
 * Confirmed service communities from the current business configuration.
 *
 * These records describe service coverage only. They must never be used to
 * imply that Revolin Roofing has a physical office in a listed municipality.
 * Copy is intentionally assessment-led: site conditions and applicable local
 * requirements are confirmed for each property before a scope is proposed.
 */
export const serviceAreas = [
  {
    slug: "vancouver",
    name: "Vancouver",
    region: "Metro Vancouver",
    confirmed: true,
    metaTitle: "Roofing Services in Vancouver, BC | Revolin Roofing",
    metaDescription:
      "Roof assessment, repair and replacement planning for Vancouver homes, with careful attention to drainage, roof transitions and local requirements.",
    intro:
      "Vancouver homes range from compact detached houses to architect-designed residences with additions, decks and low-slope sections. A useful roofing assessment starts with the actual geometry of the building rather than a one-system-fits-all recommendation.",
    localContext:
      "Projects within the City of Vancouver may be subject to requirements that differ from those used elsewhere in British Columbia. Revolin confirms the property jurisdiction and relevant project requirements before finalizing a scope.",
    assessmentFocus: [
      "Transitions between original roofs, additions and low-slope sections",
      "Valleys, wall intersections, penetrations and perimeter flashing",
      "Drainage paths that must work within compact urban lots",
    ],
    propertyContexts: [
      "Detached homes",
      "Renovated and expanded homes",
      "Mixed-slope roof assemblies",
    ],
    relatedServicePaths: [
      "/services/emergency-roof-leak-repair",
      "/services/asphalt-shingle-replacement",
      "/services/flat-roof-replacement-sbs-tpo-pvc",
    ],
    faqs: [
      {
        question: "Do Vancouver roofing projects follow the same rules as the rest of BC?",
        answer:
          "Not always. The City of Vancouver has its own building bylaw framework. The applicable requirements depend on the address and scope, so they are confirmed during planning rather than assumed from a regional checklist.",
      },
      {
        question: "Can one home need more than one roofing approach?",
        answer:
          "Yes. Additions, porches and roof decks can introduce slopes and transitions that differ from the main roof. The assessment should treat those connections as one water-management system.",
      },
    ],
  },
  {
    slug: "burnaby",
    name: "Burnaby",
    region: "Metro Vancouver",
    confirmed: true,
    metaTitle: "Roofing Services in Burnaby, BC | Revolin Roofing",
    metaDescription:
      "Roof repair, replacement and inspection planning for Burnaby homes, focused on roof geometry, drainage, access and durable transition detailing.",
    intro:
      "Burnaby's residential neighbourhoods include level sites, hillside streets and homes that have evolved through additions. That variety makes drainage, safe access and connections between roof sections important parts of the first assessment.",
    localContext:
      "A Burnaby roof can perform differently from another roof only a few neighbourhoods away because exposure, tree cover, slope and building form vary by site. Recommendations are based on observed conditions at the property.",
    assessmentFocus: [
      "Slope, access and material-handling constraints around the home",
      "Drainage at valleys, eaves, gutters and lower roof sections",
      "Connections where additions meet the original building",
    ],
    propertyContexts: ["Hillside properties", "Homes with additions", "Mature residential lots"],
    relatedServicePaths: [
      "/services/roof-health-check-maintenance",
      "/services/emergency-roof-leak-repair",
      "/services/asphalt-shingle-replacement",
    ],
    faqs: [
      {
        question: "Why does site access matter to a roofing scope?",
        answer:
          "Access affects protection, material movement and how work can be sequenced. It should be reviewed before the installation plan and scope are finalized.",
      },
      {
        question: "Should gutters be reviewed with the roof?",
        answer:
          "Drainage should be considered as a connected path from the roof surface to a suitable discharge point. The assessment documents visible gutter and downspout conditions without assuming optional drainage work is included.",
      },
    ],
  },
  {
    slug: "coquitlam",
    name: "Coquitlam",
    region: "Metro Vancouver",
    confirmed: true,
    metaTitle: "Roofing Services in Coquitlam, BC | Revolin Roofing",
    metaDescription:
      "Assessment-led roofing services for Coquitlam homes, including repair and replacement planning for complex roof planes and exposed sites.",
    intro:
      "Coquitlam properties can combine sloped sites, multi-plane homes and nearby vegetation. Roof condition, debris paths, drainage and wind-driven rain exposure are reviewed together before a repair or replacement is recommended.",
    localContext:
      "Neighbourhood elevation and exposure can change across Coquitlam. The appropriate roof system and detailing therefore depend on the individual building, not a city-wide material prescription.",
    assessmentFocus: [
      "Upper-to-lower roof drainage on multi-level homes",
      "Valleys and roof-to-wall transitions on complex roof forms",
      "Debris accumulation around gutters, penetrations and drainage points",
    ],
    propertyContexts: [
      "Multi-level homes",
      "Sloped residential sites",
      "Homes near mature vegetation",
    ],
    relatedServicePaths: [
      "/services/emergency-roof-leak-repair",
      "/services/asphalt-shingle-replacement",
      "/services/roof-health-check-maintenance",
    ],
    faqs: [
      {
        question: "Does a complex roof automatically need replacement?",
        answer:
          "No. Complexity affects diagnosis and detailing, but repair versus replacement still depends on whether the observed problem is isolated or system-wide.",
      },
      {
        question: "How is tree debris considered during an inspection?",
        answer:
          "The assessment can document areas where debris collects or slows drainage. Maintenance recommendations are then tied to what is visible at the property.",
      },
    ],
  },
  {
    slug: "surrey",
    name: "Surrey",
    region: "Metro Vancouver",
    confirmed: true,
    metaTitle: "Roofing Services in Surrey, BC | Revolin Roofing",
    metaDescription:
      "Roof inspections, repairs and replacement planning for Surrey homes, tailored to the property's roof form, exposure and drainage.",
    intro:
      "Surrey covers a broad range of residential settings, from established neighbourhoods to newer homes with large, articulated roof forms. The roof area, exposure and number of transitions all influence a practical scope.",
    localContext:
      "Because conditions vary across the city, a recommendation for one Surrey property should not be treated as a standard specification for another. Revolin documents the roof in front of us and explains the trade-offs.",
    assessmentFocus: [
      "Large roof planes and how water is directed between them",
      "Flashing at dormers, walls, chimneys and penetrations",
      "Whether damage is isolated or repeated across the roof system",
    ],
    propertyContexts: [
      "Large detached homes",
      "Newer multi-plane roofs",
      "Established neighbourhood homes",
    ],
    relatedServicePaths: [
      "/services/asphalt-shingle-replacement",
      "/services/emergency-roof-leak-repair",
      "/services/metal-roof-replacement",
    ],
    faqs: [
      {
        question: "What determines repair versus replacement on a large roof?",
        answer:
          "Size alone does not decide it. The decision depends on the covering condition, number and location of failures, transition details and whether a durable localized repair is feasible.",
      },
      {
        question: "Can an assessment be limited to one problem area?",
        answer:
          "The reported problem is investigated closely, but adjacent roof planes and drainage paths should also be reviewed because water can travel before it becomes visible indoors.",
      },
    ],
  },
  {
    slug: "richmond",
    name: "Richmond",
    region: "Metro Vancouver",
    confirmed: true,
    metaTitle: "Roofing Services in Richmond, BC | Revolin Roofing",
    metaDescription:
      "Roof assessment, repair and replacement planning for Richmond homes, with attention to drainage, low-slope details and coastal exposure.",
    intro:
      "Richmond's generally low-lying setting makes controlled roof drainage especially visible during prolonged rain. Low-slope sections, perimeter edges and discharge paths deserve the same attention as the primary roof covering.",
    localContext:
      "Coastal exposure and site openness can influence how wind-driven rain reaches roof edges and transitions. Material and detail recommendations are made only after the home's slope, form and exposure are reviewed.",
    assessmentFocus: [
      "Positive drainage and visible low points on low-slope sections",
      "Roof edges, flashings and transitions exposed to driven rain",
      "How gutters and downspouts receive water from the roof planes",
    ],
    propertyContexts: [
      "Low-slope roof sections",
      "Open residential sites",
      "Contemporary detached homes",
    ],
    relatedServicePaths: [
      "/services/flat-roof-replacement-sbs-tpo-pvc",
      "/services/roof-health-check-maintenance",
      "/services/emergency-roof-leak-repair",
    ],
    faqs: [
      {
        question: "Is a flat-looking roof actually flat?",
        answer:
          "A low-slope roof should still direct water toward drainage. The assessment looks for the designed drainage path and visible signs that water is not moving as intended.",
      },
      {
        question: "Does coastal exposure mean metal is always the right choice?",
        answer:
          "No. Roof slope, assembly design, product suitability, detailing and homeowner priorities all matter. No material is selected from location alone.",
      },
    ],
  },
  {
    slug: "north-vancouver",
    name: "North Vancouver",
    region: "Metro Vancouver",
    confirmed: true,
    metaTitle: "Roofing Services in North Vancouver, BC | Revolin Roofing",
    metaDescription:
      "Roof inspections, repair and replacement planning for North Vancouver homes, including steep sites, drainage and vegetation-related maintenance.",
    intro:
      "North Vancouver homes often sit within steep, wet and heavily vegetated settings. Safe access, concentrated runoff and debris around drainage points are practical considerations before roofing work is scoped.",
    localContext:
      "Conditions can shift between lower neighbourhoods and properties closer to the mountains. The assessment records site-specific exposure and roof access instead of relying on a generic North Shore specification.",
    assessmentFocus: [
      "Safe access to steep or elevated roof sections",
      "High-volume drainage paths between roof levels",
      "Debris and moisture retention around shaded roof areas",
    ],
    propertyContexts: ["Steep sites", "Split-level homes", "Wooded residential properties"],
    relatedServicePaths: [
      "/services/roof-health-check-maintenance",
      "/services/emergency-roof-leak-repair",
      "/services/metal-roof-replacement",
    ],
    faqs: [
      {
        question: "Can a shaded roof need a different maintenance plan?",
        answer:
          "It can. Drying conditions and debris accumulation vary by orientation and tree cover, so maintenance guidance should be based on observed roof areas.",
      },
      {
        question: "How does a steep site affect the project?",
        answer:
          "It can affect access, protection and work sequencing. Those constraints are documented before the scope is finalized rather than treated as a surprise during work.",
      },
    ],
  },
  {
    slug: "west-vancouver",
    name: "West Vancouver",
    region: "Metro Vancouver",
    confirmed: true,
    metaTitle: "Roofing Services in West Vancouver, BC | Revolin Roofing",
    metaDescription:
      "Premium roof assessment, repair and replacement planning for West Vancouver homes with complex architecture and exposed sites.",
    intro:
      "West Vancouver residences frequently use complex massing, broad glazing, terraces and a mix of steep and low-slope roof forms. Successful roofing work depends on resolving the transitions without losing sight of the architecture.",
    localContext:
      "Ocean-facing, hillside and wooded properties can experience different exposure on the same municipality's map. Revolin evaluates the individual roof geometry, access and water paths before recommending a system.",
    assessmentFocus: [
      "Connections between architectural metal, membrane and sloped roof areas",
      "Parapets, roof decks, wall interfaces and concealed drainage",
      "Access and protection planning around finished exterior spaces",
    ],
    propertyContexts: [
      "Architect-designed homes",
      "Hillside residences",
      "Mixed-material roof assemblies",
    ],
    relatedServicePaths: [
      "/services/metal-roof-replacement",
      "/services/flat-roof-replacement-sbs-tpo-pvc",
      "/services/asphalt-shingle-replacement",
    ],
    faqs: [
      {
        question: "Can a roof replacement preserve the home's architectural character?",
        answer:
          "That should be part of the recommendation. Profile, colour, edge details and visible transitions can be evaluated alongside technical suitability.",
      },
      {
        question: "Why are mixed roof systems assessed together?",
        answer:
          "Water crosses boundaries between roof areas. A detail that is correct for one surface still has to connect reliably to the next assembly.",
      },
    ],
  },
  {
    slug: "new-westminster",
    name: "New Westminster",
    region: "Metro Vancouver",
    confirmed: true,
    metaTitle: "Roofing Services in New Westminster, BC | Revolin Roofing",
    metaDescription:
      "Roof repair, inspection and replacement planning for New Westminster homes, including older assemblies, additions and compact sites.",
    intro:
      "New Westminster includes older houses, renovated homes and compact residential lots where several generations of roofing work may meet. The first task is to understand the existing assembly and where later changes connect to it.",
    localContext:
      "Age alone does not determine roof condition. Revolin looks for evidence at coverings, flashings, transitions and drainage points, then distinguishes repairable details from broader system concerns.",
    assessmentFocus: [
      "Layers of past repair or alteration visible at the roof surface",
      "Transitions at additions, porches and attached structures",
      "Material access and neighbour protection on compact lots",
    ],
    propertyContexts: ["Older detached homes", "Renovated houses", "Compact residential sites"],
    relatedServicePaths: [
      "/services/emergency-roof-leak-repair",
      "/services/roof-health-check-maintenance",
      "/services/asphalt-shingle-replacement",
    ],
    faqs: [
      {
        question: "Does an older house always need a full re-roof?",
        answer:
          "No. The visible condition and assembly performance matter more than the age of the house itself. A documented assessment is needed before that decision.",
      },
      {
        question: "What if the roof has several previous patches?",
        answer:
          "Previous work is documented as part of the diagnosis. Repeated repairs in one area may point to an unresolved transition, while unrelated patches can have different causes.",
      },
    ],
  },
  {
    slug: "port-coquitlam",
    name: "Port Coquitlam",
    region: "Metro Vancouver",
    confirmed: true,
    metaTitle: "Roofing Services in Port Coquitlam, BC | Revolin Roofing",
    metaDescription:
      "Assessment-led roof repairs, inspections and replacement planning for Port Coquitlam homes and evolving residential roof forms.",
    intro:
      "Port Coquitlam homes often combine straightforward sloped roofs with garages, additions or covered outdoor areas added over time. Those secondary roof areas can change how water reaches valleys, walls and gutters.",
    localContext:
      "A leak that appears near an addition does not necessarily begin directly above it. The assessment follows roof planes and transitions before defining a repair area.",
    assessmentFocus: [
      "Tie-ins between the main roof, garages and later additions",
      "Lower roof sections receiving water from upper roof planes",
      "Penetrations and flashing at roof-to-wall connections",
    ],
    propertyContexts: [
      "Homes with attached garages",
      "Expanded detached homes",
      "Multi-level roof drainage",
    ],
    relatedServicePaths: [
      "/services/emergency-roof-leak-repair",
      "/services/asphalt-shingle-replacement",
      "/services/roof-health-check-maintenance",
    ],
    faqs: [
      {
        question: "Why can a leak appear away from its source?",
        answer:
          "Water can travel along sheathing, framing or a roof transition before it becomes visible. Diagnosis follows the likely path instead of treating the interior stain as the source.",
      },
      {
        question: "Are garage and porch roofs part of the assessment?",
        answer:
          "They can be, especially where they connect to or receive drainage from the main roof. The requested assessment scope should identify the areas to be reviewed.",
      },
    ],
  },
  {
    slug: "port-moody",
    name: "Port Moody",
    region: "Metro Vancouver",
    confirmed: true,
    metaTitle: "Roofing Services in Port Moody, BC | Revolin Roofing",
    metaDescription:
      "Roof assessment, repair and replacement planning for Port Moody homes, with attention to wooded sites, driven rain and roof transitions.",
    intro:
      "Port Moody's inlet-facing and wooded residential settings can place moisture, debris and changing exposure on different sides of the same roof. Inspection notes should distinguish those conditions by roof plane.",
    localContext:
      "Roof orientation, nearby vegetation and building height can matter as much as the material name. Revolin documents those variables before discussing maintenance, repair or replacement options.",
    assessmentFocus: [
      "Wind-driven rain at edges, walls and penetrations",
      "Debris collection in valleys and drainage points",
      "Different weathering patterns across shaded and exposed roof planes",
    ],
    propertyContexts: ["Inlet-facing properties", "Wooded lots", "Multi-plane detached homes"],
    relatedServicePaths: [
      "/services/roof-health-check-maintenance",
      "/services/emergency-roof-leak-repair",
      "/services/metal-roof-replacement",
    ],
    faqs: [
      {
        question: "Can different sides of one roof age differently?",
        answer:
          "Yes. Sun, shade, wind and debris exposure vary by orientation. The condition report should describe each roof area rather than assign one condition to the entire roof.",
      },
      {
        question: "Is surface debris proof that the roof is failing?",
        answer:
          "No. It is a maintenance and drainage observation, not a replacement diagnosis by itself. The underlying covering and details still need to be assessed.",
      },
    ],
  },
  {
    slug: "maple-ridge",
    name: "Maple Ridge",
    region: "Lower Mainland",
    confirmed: true,
    metaTitle: "Roofing Services in Maple Ridge, BC | Revolin Roofing",
    metaDescription:
      "Roof repair, inspection and replacement planning for Maple Ridge homes, from larger lots to foothill residential sites.",
    intro:
      "Maple Ridge includes larger residential properties, wooded neighbourhoods and foothill sites where exposure and access can vary considerably. The roof and the conditions around it are reviewed as part of one project plan.",
    localContext:
      "Higher or more exposed sites may experience conditions that are not representative of lower neighbourhoods. System selection and detailing are therefore tied to the property rather than a regional assumption.",
    assessmentFocus: [
      "Access, staging and protection on larger or sloped sites",
      "Drainage and debris management near mature vegetation",
      "Exposure at ridges, eaves and roof edges",
    ],
    propertyContexts: ["Larger residential lots", "Foothill neighbourhoods", "Wooded properties"],
    relatedServicePaths: [
      "/services/asphalt-shingle-replacement",
      "/services/roof-health-check-maintenance",
      "/services/emergency-roof-leak-repair",
    ],
    faqs: [
      {
        question: "Does a larger property change the roof recommendation?",
        answer:
          "Not by itself. It can change access and project logistics, while the roof system recommendation still depends on slope, assembly, exposure and condition.",
      },
      {
        question: "How are more exposed roof edges reviewed?",
        answer:
          "The assessment documents visible edge condition, fastening-related concerns and transition details, then relates any recommendation to the selected system and applicable requirements.",
      },
    ],
  },
  {
    slug: "delta",
    name: "Delta",
    region: "Lower Mainland",
    confirmed: true,
    metaTitle: "Roofing Services in Delta, BC | Revolin Roofing",
    metaDescription:
      "Roof inspections, repair and replacement planning across Delta, with site-specific attention to wind exposure, drainage and roof form.",
    intro:
      "Delta's communities include coastal, agricultural-edge and inland residential settings. Those different exposures make it important to assess the individual site's wind, drainage and roof geometry before selecting a solution.",
    localContext:
      "A Tsawwassen, Ladner or North Delta property should not receive a recommendation based only on the municipality name. Revolin bases the scope on the building and conditions observed at the address.",
    assessmentFocus: [
      "Wind exposure at eaves, ridges and perimeter details",
      "Drainage capacity and discharge from broad roof planes",
      "Material condition across differently exposed elevations",
    ],
    propertyContexts: ["Coastal neighbourhoods", "Open residential sites", "Inland detached homes"],
    relatedServicePaths: [
      "/services/roof-health-check-maintenance",
      "/services/asphalt-shingle-replacement",
      "/services/metal-roof-replacement",
    ],
    faqs: [
      {
        question: "Is the same roof system suitable across all of Delta?",
        answer:
          "There is no automatic city-wide choice. Slope, exposure, architecture, drainage and budget all influence the appropriate system.",
      },
      {
        question: "What is checked after strong wind?",
        answer:
          "A targeted assessment can review accessible roof edges, coverings, flashings and penetrations for visible change. Safety comes first; homeowners should not climb onto a wet or damaged roof.",
      },
    ],
  },
  {
    slug: "langley",
    name: "Langley",
    region: "Lower Mainland",
    confirmed: true,
    metaTitle: "Roofing Services in Langley, BC | Revolin Roofing",
    metaDescription:
      "Roof assessment, repair and replacement planning for Langley homes, including larger roof areas, additions and open-site exposure.",
    intro:
      "Langley properties can include larger detached homes, broad roof spans and multiple attached structures. A clear scope separates each roof area while resolving how they drain and connect.",
    localContext:
      "Open sites and established treed neighbourhoods present different maintenance and exposure considerations. Revolin records the site's actual conditions before recommending materials or repair limits.",
    assessmentFocus: [
      "Broad roof planes and long drainage runs",
      "Connections between the home, garage and covered outdoor areas",
      "Differences between open exposure and tree-sheltered sections",
    ],
    propertyContexts: [
      "Larger detached homes",
      "Homes with attached structures",
      "Open residential sites",
    ],
    relatedServicePaths: [
      "/services/asphalt-shingle-replacement",
      "/services/gutter-replacement",
      "/services/metal-roof-replacement",
    ],
    faqs: [
      {
        question: "Can a project be phased by roof area?",
        answer:
          "Sometimes, but only if the assemblies and transitions allow a durable stopping point. The scope should explain how staged work affects connected areas.",
      },
      {
        question: "Does a broad roof plane require one particular material?",
        answer:
          "No. Geometry is one input. Slope, assembly design, exposure, appearance, maintenance priorities and budget must be considered together.",
      },
    ],
  },
  {
    slug: "white-rock",
    name: "White Rock",
    region: "Lower Mainland",
    confirmed: true,
    metaTitle: "Roofing Services in White Rock, BC | Revolin Roofing",
    metaDescription:
      "Roof inspections, repair and replacement planning for White Rock homes, with attention to coastal exposure, edges and water-shedding details.",
    intro:
      "White Rock homes can combine ocean-facing exposure with contemporary roof forms, balconies and low-slope sections. Edges, wall transitions and drainage details are reviewed in the context of that architecture.",
    localContext:
      "Coastal conditions do not make one roof product universally correct. Product suitability, system design and visible detailing are considered alongside slope and homeowner priorities.",
    assessmentFocus: [
      "Perimeter and transition details exposed to driven rain",
      "Low-slope drainage around parapets, curbs and adjacent walls",
      "Compatibility of visible metal details with the selected assembly",
    ],
    propertyContexts: ["Ocean-facing homes", "Contemporary residences", "Mixed-slope roof designs"],
    relatedServicePaths: [
      "/services/metal-roof-replacement",
      "/services/flat-roof-replacement-sbs-tpo-pvc",
      "/services/roof-health-check-maintenance",
    ],
    faqs: [
      {
        question: "Does ocean exposure automatically rule out a roofing material?",
        answer:
          "Not automatically. The complete system, product limitations, coating or finish, fastening and transition details all need to be reviewed for the property.",
      },
      {
        question: "Why are balcony and parapet connections important?",
        answer:
          "They can interrupt simple water shedding and introduce wall, curb or membrane transitions. Those interfaces should be included in the documented assessment.",
      },
    ],
  },
] as const satisfies readonly ServiceArea[];

export type ServiceAreaSlug = (typeof serviceAreas)[number]["slug"];

export const confirmedServiceAreas = serviceAreas.filter((area) => area.confirmed);

export function findServiceArea(slug: string | undefined): ServiceArea | undefined {
  return serviceAreas.find((area) => area.slug === slug);
}
